import * as React from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { AlertTriangle, RotateCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Spinner } from "@/components/ui/spinner";
import { ProvisioningStatusPill } from "@/components/ui/status-pill";
import { getDomainProvisioning, retryProvisioning } from "@/api/domains";
import { apiErrorMessage } from "@/lib/api";
import { formatRelativeTime } from "@/lib/utils";
import type { ProvisioningStep } from "@/types/api";

const STEP_LABEL: Record<ProvisioningStep, string> = {
    PURCHASE: "Register domain at Porkbun",
    ADD_ZONE: "Create Cloudflare zone",
    DELEGATE_NS: "Point nameservers to Cloudflare",
    ATTACH_WORKER: "Attach the page worker + DNS",
    CREATE_GA: "Create the Google Analytics property",
    SYNC_KV: "Publish edge config",
};

/**
 * Provisioning diagnosis + recovery for a domain. Renders nothing while a
 * domain is healthy and fully provisioned with no job history worth showing;
 * surfaces the failed step + error + a "Retry provisioning" action when the
 * chain stalled, and a live progress list while it's still running.
 */
export function DomainProvisioningCard({ domainId }: { domainId: number }): React.ReactNode {
    const queryClient = useQueryClient();

    const query = useQuery({
        queryKey: ["domain-provisioning", domainId],
        queryFn: () => getDomainProvisioning(domainId),
        enabled: Number.isFinite(domainId),
        refetchInterval: (q) => (q.state.data?.inProgress ? 3000 : false),
    });

    const retryMut = useMutation({
        mutationFn: () => retryProvisioning(domainId),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["domain", domainId] });
            queryClient.invalidateQueries({ queryKey: ["domain-provisioning", domainId] });
            queryClient.invalidateQueries({ queryKey: ["domains"] });
            toast.success("Provisioning restarted — watch the steps below");
        },
        onError: (err) => toast.error(apiErrorMessage(err, "Retry failed")),
    });

    const data = query.data;
    if (!data) return null;

    // Only worth a card when something needs attention or is moving: a retryable
    // failure, an in-flight run, or any dead-lettered job in the history. A
    // clean fully-provisioned domain shows nothing.
    const hasFailure = data.jobs.some((j) => j.status === "DEAD_LETTER" || j.status === "FAILED");
    if (!data.inProgress && !data.retryable && !hasFailure) return null;

    const failedJob = data.jobs.find((j) => j.status === "DEAD_LETTER" || j.status === "FAILED");

    return (
        <Card data-testid="provisioning-card">
            <CardHeader>
                <CardTitle className="flex items-center gap-2">
                    {data.retryable && <AlertTriangle className="h-4 w-4 text-destructive" />}
                    Provisioning
                </CardTitle>
                <CardDescription>
                    {data.inProgress
                        ? "Setting this domain up — steps run automatically every few seconds."
                        : data.retryable
                          ? "Provisioning stopped before this domain went live. You can resume it from the failed step."
                          : "Provisioning history for this domain."}
                </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
                {data.retryable && failedJob && (
                    <div className="rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm">
                        <div className="font-medium">
                            Stopped at: {STEP_LABEL[failedJob.step] ?? failedJob.step}
                        </div>
                        {failedJob.error && (
                            <p className="mt-1 whitespace-pre-wrap break-words font-mono text-xs text-muted-foreground">
                                {failedJob.error}
                            </p>
                        )}
                        <p className="mt-2 text-xs text-muted-foreground">
                            Retrying is safe even if this domain was already registered — each step
                            checks what already exists before acting. Prefer this over deleting a
                            domain you may have paid for.
                        </p>
                        <Button
                            className="mt-3"
                            onClick={() => retryMut.mutate()}
                            disabled={retryMut.isPending}
                            data-testid="retry-provisioning-button"
                        >
                            {retryMut.isPending ? <Spinner size="sm" /> : <RotateCw className="h-4 w-4" />}
                            Retry provisioning
                        </Button>
                    </div>
                )}

                <ol className="space-y-1.5">
                    {data.jobs.map((job) => (
                        <li key={job.id} className="flex items-center justify-between gap-3 text-sm">
                            <span className="flex items-center gap-2">
                                <ProvisioningStatusPill status={job.status} />
                                <span>{STEP_LABEL[job.step] ?? job.step}</span>
                                {job.attemptCount > 1 && (
                                    <span className="text-xs text-muted-foreground">
                                        · {job.attemptCount} attempts
                                    </span>
                                )}
                            </span>
                            <span className="shrink-0 text-xs text-muted-foreground">
                                {formatRelativeTime(job.completedAt ?? job.startedAt ?? job.scheduledAt)}
                            </span>
                        </li>
                    ))}
                </ol>
            </CardContent>
        </Card>
    );
}
